const PENDING_STATUSES = ['PENDING_SYNC', 'NOT_SYNCED'];
// Stop polling a row that's still pending after this long — a permanently
// stuck sync shouldn't poll the backend every 3s for the rest of the session.
const MAX_POLL_DURATION_MS = 2 * 60 * 1000;
// Tracks when each query first observed a pending row, keyed by caller-chosen tracking key.
const pendingSince = new Map<string, number>();

export function hasPendingSyncStatus(data: Record<string, unknown>[] | undefined): boolean {
  return !!data?.some((item) => PENDING_STATUSES.includes(String(item.syncStatus ?? '')));
}

/**
 * React Query `refetchInterval` that polls every 3s while `trackingKey`'s data has a
 * pending row, capped at {@link MAX_POLL_DURATION_MS} from the first pending observation.
 */
export function pendingSyncRefetchInterval(trackingKey: string) {
  return (query: { state: { data: unknown } }): number | false => {
    const data = query.state.data as Record<string, unknown>[] | undefined;
    if (!hasPendingSyncStatus(data)) {
      pendingSince.delete(trackingKey);
      return false;
    }
    const since = pendingSince.get(trackingKey) ?? Date.now();
    pendingSince.set(trackingKey, since);
    return Date.now() - since < MAX_POLL_DURATION_MS ? 3000 : false;
  };
}

export function isPendingSyncStuck(
  trackingKey: string,
  data: Record<string, unknown>[] | undefined
): boolean {
  const since = pendingSince.get(trackingKey);
  return hasPendingSyncStatus(data) && since !== undefined && Date.now() - since >= MAX_POLL_DURATION_MS;
}

/**
 * Restarts the poll budget for one tracking key, or every key when called with none.
 *
 * <p>Call this whenever rows that were pending are known to have just changed status
 * server-side (e.g. a draft push) — otherwise a row that was pending for the whole
 * 2-minute budget while staged in draft mode would never poll again after publish.
 */
export function resetPendingSyncPolling(trackingKey?: string): void {
  if (trackingKey) {
    pendingSince.delete(trackingKey);
  } else {
    pendingSince.clear();
  }
}
